// Game-rules / deck-rules / campaigns / skirmish.
//
// Types and the pure resolver are shared between client and server. The
// fs-based loader that reads data/{game-rules,deck-rules,campaigns}/*.json
// lives in server/rules-loader.ts; the client receives the already-resolved
// bundle from `GET /api/rules/bundle`.

import type { CardDef, DeckDef, Rarity } from '../types.js';

// ── Rule shapes ─────────────────────────────────────────────────

export interface StartingRing {
  /** Per-slot card slugs. `null` = empty. Length up to ringSlots; extra
   *  entries are ignored. Cards are instantiated fresh (not drawn from the
   *  deck) and placed in the ring at game start with `isFresh = false`,
   *  ready to act on turn 1. On-play abilities don't fire. */
  player: (string | null)[];
  opponent: (string | null)[];
}

export interface GameRule {
  id: string;
  /** HP cap and the bar denominator both players' HP renders against. */
  maxHp: number;
  /** Player's starting HP. `null` defaults to `maxHp`. */
  initialPlayerHp: number | null;
  /** Opponent's starting HP. `null` defaults to `maxHp`. */
  initialOpponentHp: number | null;
  startingHandSize: number;
  startingSpirit: number;
  startingClock: number;
  /** Upper bound the play-clock wraps around. At maxClock, +1 brings
   *  it back to 1 (never to 0, which is only the pre-turn-1 state).
   *  Undefined = wrap at 12, the historical default. Rapid-cycle
   *  tournaments set this lower (e.g. 3) to make high-clock cards
   *  effectively unplayable and force tempo-heavy decks. */
  maxClock?: number;
  /** When true, the player's opening hand is guaranteed to contain at least
   *  one playable character (clock ≤ 1, spirit ≥ 0). Implemented by swapping
   *  a deeper playable card up into the hand if the natural top-N draw has
   *  none. Useful for the intro / tutorial flow where a stuck opening would
   *  give a new player nothing to do. */
  requirePlayable: boolean;
  /** Optional pre-placed ring cards for one or both sides. */
  startingRing: StartingRing | null;
  /** Who goes first. `null` = random coin flip at game start. The "second
   *  player" (whoever didn't go first) advances the play-clock at the start
   *  of every one of their turns. */
  startingPlayer: 'player' | 'opponent' | null;
  /** Whether the first player draws on their first turn. Default `true`:
   *  P1 draws like any other turn. Setting `false` reproduces the prior
   *  rule where P1 skipped their first-turn draw to compensate for
   *  first-mover tempo — empirical mirror-match data showed that
   *  compensation overshot and gave the second player a ~5-7pt win-rate
   *  edge against medium/hard AI, which giving P1 the draw effectively
   *  closes (`tools/first-player-advantage.ts`). */
  firstPlayerDrawsOnFirstTurn?: boolean;
}

export interface RarityRestriction {
  rarity: Rarity;
  op: '<=' | '>=';
  count: number;
}

export interface CardRestriction {
  attribute: 'power' | 'clock' | 'spirit';
  op: '<=' | '>=';
  value: number;
}

export interface DeckRule {
  id: string;
  deckSize: number;
  rarityRestrictions: RarityRestriction[];
  cardRestrictions: CardRestriction[];
  allowedCardTypes: ('character' | 'action')[] | null;
  vanillaOnly: boolean;
}

// ── Reward shape (shared by stages and skirmish) ───────────────

export interface Reward {
  coins?: number;
  gems?: number;
  cards?: string[];
  pack?: {
    cardCount: number;
    guaranteed?: Rarity[];
    rarityWeights?: Partial<Record<Rarity, number>>;
  };
  /** Generic single-use credits granted alongside coins/gems/cards.
   *  Each entry adds `count` to the player's `user_tickets[id]` row.
   *  Display names live in `src/shared/tickets.ts`. */
  tickets?: { id: string; count: number }[];
}

// ── Campaign / Stage / Skirmish ────────────────────────────────

export interface Stage {
  id: string;                          // pure GUID — used as DB key when progress tracking lands.
  name: string;
  description: string;
  opponent: string | null;             // optional card slug for the "boss face"
  opponentDeck: string | null;         // optional deck name; null → tiered AI pick
  /** Card slug for the PLAYER'S portrait in the pre-battle ceremony.
   *  Falls back to `Campaign.protagonist` when null/missing. Lets a
   *  multi-era campaign POV swap protagonists per stage (e.g. a
   *  Crusades campaign that alternates between Richard and Saladin)
   *  while keeping a single default for the common case. */
  protagonist?: string | null;
  gameRule: string | null;             // overrides campaign.gameRule when set
  deckRule: string | null;             // overrides campaign.deckRule when set
  /** Optional pre-battle briefing shown as a modal the player must dismiss
   *  with OK before the coin flip. Use it to explain non-obvious starting
   *  conditions (a pre-placed boss unit on the ring, an HP swing, an unusual
   *  starting clock) and to set the historical scene. `null` / missing skips
   *  the popup entirely — used for stages whose `description` already
   *  conveys everything the player needs to know. */
  introMessage: string | null;
  /** Bullet-list of explicit mission conditions shown on the stage tile,
   *  under the prose description. Hand-written rather than derived from
   *  the underlying game-rule so authors can phrase them in terms the
   *  player actually cares about ("Pompey starts with a Cataphract") rather
   *  than mechanical jargon ("startingRing.opponent[1] = cataphract"). Each
   *  entry is one line. `null` / empty = no list rendered. */
  missionConditions: string[] | null;
  firstWinReward: Reward | null;
  lossReward: Reward | null;
  replayWinReward: Reward | null;
}

export interface Campaign {
  id: string;
  name: string;
  description: string;
  hidden: boolean;
  /** Stage IDs that must all be in the player's `beatenStageIds` for this
   *  campaign to be unlocked. Empty/missing = always unlocked. References
   *  stages from any campaign (typically the final stage of a prerequisite
   *  campaign). */
  requiredCompletions?: string[];
  /** Sort key for the campaign list. Lower = earlier. Missing = sorted to
   *  the end. Lets the file-system readdir order (which the loader yields
   *  by name) be overridden so newer easier campaigns can lead the list
   *  without renaming files. */
  order?: number;
  gameRule: string;                    // game-rule id (post-$extends-resolution lookup)
  deckRule: string;                    // deck-rule id
  /** Default card slug for the PLAYER'S portrait in the pre-battle
   *  ceremony — the historical figure the player is roleplaying for
   *  this campaign (Caesar's Wars → julius-caesar, Genpei War →
   *  tomoe-gozen). Individual stages can override via `Stage.protagonist`.
   *  Null/missing → no portrait (skirmish fallback applies). */
  protagonist?: string | null;
  stages: Stage[];
}

export interface Skirmish {
  gameRule: string;
  deckRule: string;
  winReward: Reward;
  lossReward: Reward;
}

/** Progression gates that control when secondary game modes unlock.
 *  Data-driven so we can retarget which content gates what without
 *  touching the menu code. Loaded from data/progression.json. */
export interface Progression {
  /** Stage IDs that must ALL be in the player's `beatenStageIds` for
   *  the Tournaments menu entry (and its main-menu widget) to appear.
   *  Empty = tournaments always available. Currently the final stage
   *  of the Genpei campaign ("Tomoe's Last Stand"), so a new player is
   *  steered through the campaign before the tournament ladder opens. */
  tournamentUnlockStageIds: string[];
  /** Stage IDs that must ALL be in the player's `beatenStageIds` for
   *  the Auction House menu entry to appear. Empty = always available.
   *  Currently the final stage of the Caesar's Wars campaign — the
   *  auction house is a secondary economy surface that assumes the
   *  player has built up a collection worth trading. */
  auctionHouseUnlockStageIds?: string[];
}

export interface RulesBundle {
  gameRules: Record<string, GameRule>;
  deckRules: Record<string, DeckRule>;
  campaigns: Record<string, Campaign>;
  skirmish: Skirmish;
  progression: Progression;
}

// ── Resolver: $extends inheritance ────────────────────────────

/** Resolve a rule by id from a raw map, recursively merging $extends parents
 *  (parent first, child overrides). The merge is SHALLOW (top-level keys
 *  only) — a child that overrides a nested object like `search` or
 *  `weights` must restate that whole object, not just the changed field.
 *  Throws on cycles or missing parents. Used for game-rules, deck-rules,
 *  and AI tuning profiles. */
export function resolveRule<T extends { id: string }>(
  rawById: Record<string, unknown>,
  id: string,
  kind: 'game-rule' | 'deck-rule' | 'ai-profile',
): T {
  return resolveInternal(rawById, id, kind, new Set()) as T;
}

function resolveInternal(
  rawById: Record<string, unknown>,
  id: string,
  kind: string,
  seen: Set<string>,
): Record<string, unknown> {
  if (seen.has(id)) {
    throw new Error(`Cycle in ${kind} $extends chain: ${[...seen, id].join(' → ')}`);
  }
  const raw = rawById[id] as Record<string, unknown> | undefined;
  if (!raw) {
    throw new Error(`${kind} '${id}' not found`);
  }
  const nextSeen = new Set(seen);
  nextSeen.add(id);

  const { $extends: parentId, ...rest } = raw as { $extends?: string | null } & Record<string, unknown>;
  if (parentId == null) return { ...rest };
  const parent = resolveInternal(rawById, parentId, kind, nextSeen);
  return { ...parent, ...rest };
}

// ── Deck validator ────────────────────────────────────────────

/** Returns human-readable violation messages; empty array means the deck
 *  satisfies the rule. Default rule has no restrictions, so this returns []
 *  for every deck. Caller surfaces violations to the user. */
export function validateDeckAgainstRule(
  deck: DeckDef,
  catalog: Map<string, CardDef>,
  rule: DeckRule,
): string[] {
  const errors: string[] = [];

  const totalCards = deck.cards.reduce((sum, c) => sum + c.count, 0);
  if (totalCards !== rule.deckSize) {
    errors.push(`Deck must have exactly ${rule.deckSize} cards (has ${totalCards}).`);
  }

  const rarityCounts: Record<Rarity, number> = { common: 0, uncommon: 0, rare: 0, legendary: 0 };
  for (const entry of deck.cards) {
    const def = catalog.get(entry.id);
    if (!def) continue;
    rarityCounts[def.rarity] += entry.count;
  }
  for (const restr of rule.rarityRestrictions) {
    const actual = rarityCounts[restr.rarity];
    const ok = restr.op === '<=' ? actual <= restr.count : actual >= restr.count;
    if (!ok) {
      errors.push(`Deck has ${actual} ${restr.rarity} card(s) but must have ${restr.op} ${restr.count}.`);
    }
  }

  for (const entry of deck.cards) {
    const def = catalog.get(entry.id);
    if (!def) continue;

    if (rule.allowedCardTypes && !rule.allowedCardTypes.includes(def.type)) {
      errors.push(`"${def.name}" is type ${def.type}; only ${rule.allowedCardTypes.join(', ')} allowed.`);
    }

    if (rule.vanillaOnly && def.type === 'character' && def.ability) {
      errors.push(`"${def.name}" has an ability; vanilla-only required.`);
    }

    for (const cr of rule.cardRestrictions) {
      const actual = readAttribute(def, cr.attribute);
      if (actual === undefined) continue;
      const ok = cr.op === '<=' ? actual <= cr.value : actual >= cr.value;
      if (!ok) {
        errors.push(`"${def.name}" has ${cr.attribute} ${actual}; must be ${cr.op} ${cr.value}.`);
      }
    }
  }

  return errors;
}

function readAttribute(def: CardDef, attr: 'power' | 'clock' | 'spirit'): number | undefined {
  if (attr === 'power') return def.type === 'character' ? def.power : undefined;
  if (attr === 'clock') return def.clock ?? 1;
  if (attr === 'spirit') return def.spirit;
  return undefined;
}
